import React, { useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import BookingForm from '../BookingForm';
import SEO from '../SEO.js';
import './ServicePages.css';

const LabTests = () => {
  const [showBookingForm, setShowBookingForm] = useState(false);

  const handleBookNow = () => {
    setShowBookingForm(true);
  };

  const handleCloseBooking = () => {
    setShowBookingForm(false);
    // Fix button color after form closes
    const buttons = document.querySelectorAll('.book-now-hero-btn, .cta-button, .sidebar-book-btn');
    buttons.forEach(button => {
      button.style.backgroundColor = 'var(--primary-color)';
      button.style.border = 'none';
      button.style.color = 'var(--white-color)';
    });
  };

  const benefits = [
    'Convenient sample collection at home',
    'No waiting in queues or hospitals',
    'Ideal for elderly, busy professionals, and mobility-challenged patients',
    'Same accuracy as laboratory visits',
    'Digital reports delivered quickly',
    'Fasting tests without the hassle of travel'
  ];

  const testCategories = [
    {
      name: 'Routine Health Checkups',
      description: 'Complete blood count, lipid profile, liver function, kidney function, and more.'
    },
    {
      name: 'Diabetes Management',
      description: 'Blood glucose, HbA1c, insulin levels, and comprehensive diabetes profiles.'
    },
    {
      name: 'Cardiac Health',
      description: 'Lipid profile, cardiac markers, ECG, and specialized heart health panels.'
    },
    {
      name: 'Specialized Diagnostics',
      description: 'Thyroid function, vitamin deficiencies, hormone panels, and allergy testing.'
    }
  ];

  return (
    <div className="service-page lab-tests-page">
      <SEO 
        title="Lab Tests at Home in Bangalore"
        description="Convenient lab tests at home in Bangalore. We offer blood sample collection, health checkups, and diagnostic tests with quick and accurate reports."
        keywords="lab tests at home Bangalore, blood test at home, health checkup packages, home sample collection, diagnostic centre Bangalore"
      />
      <div className="service-hero">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12}>
              <div className="service-hero-content">
                <h1>Home Lab Test Services</h1>
                <p>Accurate diagnostic testing in the comfort of your home</p>
                <Button className="book-now-hero-btn" onClick={handleBookNow}>
                  Book Appointment
                </Button>
              </div>
            </Col>
            <Col lg={6} md={12}>
              <div className="service-hero-image">
                <img src="/images/lab.jpg" alt="Lab Test Services" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="service-content">
        <Row>
          <Col lg={8} md={12}>
            <div className="service-description">
              <h2>Convenient Diagnostic Testing at Your Doorstep</h2>
              <p>
                Our home lab testing service brings the diagnostic laboratory to your doorstep. Our trained phlebotomists 
                visit your home to collect samples for a wide range of laboratory tests, eliminating the need for you to 
                travel to a diagnostic center or hospital.
              </p>
              <p>
                We use state-of-the-art equipment and follow strict protocols for sample collection, transportation, and 
                processing to ensure accurate and reliable results. All samples are analyzed in certified laboratories, 
                and reports are delivered to you digitally and physically as per your preference.
              </p>
              
              <h3>Our Lab Test Categories</h3>
              <Row className="treatment-cards">
                {testCategories.map((category, index) => (
                  <Col md={6} key={index} className="mb-4">
                    <Card className="treatment-card h-100">
                      <Card.Body>
                        <Card.Title>{category.name}</Card.Title>
                        <Card.Text>{category.description}</Card.Text>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              
              <h3 className="mt-4">Our Lab Testing Process</h3>
              <Row className="process-steps">
                <Col md={3} sm={6} className="text-center mb-4">
                  <div className="process-step">
                    <div className="step-number">1</div>
                    <h5>Book Test</h5>
                    <p>Schedule your test online or by phone</p>
                  </div>
                </Col>
                <Col md={3} sm={6} className="text-center mb-4">
                  <div className="process-step">
                    <div className="step-number">2</div>
                    <h5>Sample Collection</h5>
                    <p>Phlebotomist visits your home</p>
                  </div>
                </Col>
                <Col md={3} sm={6} className="text-center mb-4">
                  <div className="process-step">
                    <div className="step-number">3</div>
                    <h5>Lab Analysis</h5>
                    <p>Samples analyzed in certified labs</p>
                  </div>
                </Col>
                <Col md={3} sm={6} className="text-center mb-4">
                  <div className="process-step">
                    <div className="step-number">4</div>
                    <h5>Report Delivery</h5>
                    <p>Digital reports sent securely</p>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          
          <Col lg={4} md={12}>
            <div className="service-sidebar">
              <Card className="benefits-card">
                <Card.Header as="h5">Benefits of Home Lab Tests</Card.Header>
                <Card.Body>
                  <ul className="benefits-list">
                    {benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                  <div className="d-grid gap-2 mt-4">
                    <Button variant="primary" onClick={handleBookNow} className="sidebar-book-btn">
                      Book Your Test Now
                    </Button>
                  </div>
                </Card.Body>
              </Card>
              
              <Card className="why-choose-card mt-4">
                <Card.Header as="h5">Why Choose Our Lab Services?</Card.Header>
                <Card.Body>
                  <ul className="why-choose-list">
                    <li>Trained and experienced phlebotomists</li>
                    <li>NABL accredited laboratory partners</li>
                    <li>Strict adherence to sample handling protocols</li>
                    <li>Quick turnaround time for results</li>
                    <li>Doctor consultation for report interpretation</li>
                  </ul>
                </Card.Body>
              </Card>
            </div>
          </Col>
        </Row>

        <Row className="mt-5">
          <Col md={12}>
            <div className="cta-section text-center">
              <h3>Need Diagnostic Tests?</h3>
              <p>Skip the lab visit. Get accurate tests done in the comfort of your home.</p>
              <Button size="lg" onClick={handleBookNow} className="cta-button">
                Schedule Your Home Lab Test
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
      
      <BookingForm 
        show={showBookingForm} 
        handleClose={handleCloseBooking} 
        defaultService="Lab Tests"
      />
    </div>
  );
};

export default LabTests;
